import { Injectable, signal, effect } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, delay } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppConfig, ThemeConfig, ThemeMode } from '../models/config.model';
import { ConfigUrls, ApiUrlBuilder } from '../components/enums/api-urls/api-urls.enum';
import { API_CONFIG } from '../config/api.config';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private baseUrl = API_CONFIG.getBaseUrl();
  private configUrl = ApiUrlBuilder.buildFullUrl(ConfigUrls.INITIAL_DATA, this.baseUrl);
  private appConfig = signal<AppConfig | null>(null);
  private isLoading = signal<boolean>(false);
  private error = signal<string | null>(null);
  private currentMode = signal<ThemeMode>('light');

  public readonly config = this.appConfig.asReadonly();
  public readonly loading = this.isLoading.asReadonly();
  public readonly configError = this.error.asReadonly();
  public readonly mode = this.currentMode.asReadonly();

  constructor(private http: HttpClient) {
    // Load saved mode from localStorage
    const savedMode = this.loadSavedMode();
    this.currentMode.set(savedMode);
    
    // Apply theme when config is loaded
    effect(() => {
      const config = this.appConfig();
      const mode = this.currentMode();
      if (config?.theme) {
        this.applyTheme(config.theme, mode);
      }
    });
    
    // Apply mode when it changes
    effect(() => {
      const mode = this.currentMode();
      this.applyMode(mode);
    });
  }

  loadConfig(): Observable<AppConfig> {
    this.isLoading.set(true);
    this.error.set(null);

    // Try to load saved theme from localStorage
    const savedTheme = this.loadSavedTheme();
    
    // Default fallback config with 5-color theme
    const defaultTheme: ThemeConfig = savedTheme || {
      color1: '#2563eb',      // Primary color (blue)
      color2: '#1e40af',      // Secondary color (darker blue)
      color3: '#3b82f6',      // Accent color (lighter blue)
      color4: '#10b981',      // Success color (green)
      color5: '#f59e0b',      // Warning color (orange)
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      buttonColor: '#2563eb',
      buttonTextColor: '#ffffff',
      primaryColor: '#2563eb',  // Legacy support
      secondaryColor: '#1e40af' // Legacy support
    };

    const defaultConfig: AppConfig = {
      logo: '/HCMS_LOGO_2.png', // Default logo path from public folder
      backgroundImage: '/HCMS_BACKGROUND_IMG.png', // Default background
      theme: defaultTheme,
      companyName: 'HCMS',
      poweredBy: 'HCMS',
      mode: this.currentMode() || 'light'
    };

    // TODO: Replace with actual API call when backend is ready
    // Uncomment the code below and comment out the mock implementation
   
    return this.http.get<AppConfig>(this.configUrl, {
  headers: new HttpHeaders({
    'x-tenant-id': 'HOS-101'
  })
}).pipe(
      tap(config => {
        this.appConfig.set(config);
        this.isLoading.set(false);
      }),
      catchError(error => {
        console.error('Failed to load config from API:', error);
        this.error.set(error.message || 'Failed to load configuration');
        this.isLoading.set(false);
        // Use default config as fallback
        this.appConfig.set(defaultConfig);
        return of(defaultConfig);
      })
    );
  

    // Mock implementation - returns config with simulated delay
    // Replace this with the API call above when ready
    return of(defaultConfig).pipe(
      delay(100), // Simulate network delay
      tap(config => {
        this.appConfig.set(config);
        this.isLoading.set(false);
      }),
      catchError(error => {
        this.error.set(error.message || 'Failed to load configuration');
        this.isLoading.set(false);
        this.appConfig.set(defaultConfig);
        return of(defaultConfig);
      })
    );
  }

  updateTheme(theme: ThemeConfig): void {
    const currentConfig = this.appConfig();
    if (currentConfig) {
      const updatedConfig: AppConfig = {
        ...currentConfig,
        theme: { ...theme }
      };
      this.appConfig.set(updatedConfig);
      this.applyTheme(theme, this.currentMode());
    } else {
      // If no config exists, create a minimal one
      const defaultConfig: AppConfig = {
        logo: '/HCMS_LOGO_2.png',
        backgroundImage: '/HCMS_BACKGROUND_IMG.png',
        theme: { ...theme },
        companyName: 'HCMS',
        poweredBy: 'HCMS'
      };
      this.appConfig.set(defaultConfig);
      this.applyTheme(theme, this.currentMode());
    }
  }

  saveTheme(theme: ThemeConfig): void {
    // Save to localStorage
    try {
      localStorage.setItem('app-theme', JSON.stringify(theme));
      this.updateTheme(theme);
    } catch (error) {
      console.error('Failed to save theme:', error);
    }
  }

  private loadSavedTheme(): ThemeConfig | null {
    try {
      const savedTheme = localStorage.getItem('app-theme');
      if (savedTheme) {
        return JSON.parse(savedTheme);
      }
    } catch (error) {
      console.error('Failed to load saved theme:', error);
    }
    return null;
  }

  setMode(mode: ThemeMode): void {
    this.currentMode.set(mode);
    localStorage.setItem('app-theme-mode', mode);
    
    const config = this.appConfig();
    if (config) {
      const updatedConfig: AppConfig = {
        ...config,
        mode: mode
      };
      this.appConfig.set(updatedConfig);
    }
    
    this.applyMode(mode);
    
    // Reapply theme with new mode
    const theme = this.appConfig()?.theme;
    if (theme) {
      this.applyTheme(theme, mode);
    }
  }

  toggleMode(): void {
    const newMode: ThemeMode = this.currentMode() === 'light' ? 'dark' : 'light';
    this.setMode(newMode);
  }

  private loadSavedMode(): ThemeMode {
    try {
      const savedMode = localStorage.getItem('app-theme-mode');
      if (savedMode === 'dark' || savedMode === 'light') {
        this.currentMode.set(savedMode);
        return savedMode;
      }
    } catch (error) {
      console.error('Failed to load saved mode:', error);
    }
    return 'light';
  }

  private applyMode(mode: ThemeMode): void {
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  private applyTheme(theme: ThemeConfig, mode: ThemeMode = 'light'): void {
    const root = document.documentElement;
    
    // Map 5-color system to CSS variables
    const color1 = theme.color1 || theme.primaryColor || '#2563eb';
    const color2 = theme.color2 || theme.secondaryColor || '#1e40af';
    const color3 = theme.color3 || '#3b82f6';
    const color4 = theme.color4 || '#10b981';
    const color5 = theme.color5 || '#f59e0b';
    
    // Set all 5 colors as CSS variables
    root.style.setProperty('--theme-color-1', color1);
    root.style.setProperty('--theme-color-2', color2);
    root.style.setProperty('--theme-color-3', color3);
    root.style.setProperty('--theme-color-4', color4);
    root.style.setProperty('--theme-color-5', color5);
    
    // Legacy support
    root.style.setProperty('--primary-color', color1);
    root.style.setProperty('--secondary-color', color2);
    root.style.setProperty('--accent-color', color3);
    root.style.setProperty('--success-color', color4);
    root.style.setProperty('--warning-color', color5);
    
    // Apply colors based on mode
    if (mode === 'dark') {
      const darkBg = theme.darkBackgroundColor || '#1f2937';
      const darkText = theme.darkTextColor || '#f9fafb';
      const darkCard = theme.darkCardBackground || '#374151';
      const darkBorder = theme.darkBorderColor || '#4b5563';
      
      root.style.setProperty('--background-color', darkBg);
      root.style.setProperty('--text-color', darkText);
      root.style.setProperty('--card-background', darkCard);
      root.style.setProperty('--border-color', darkBorder);
      root.style.setProperty('--surface-color', darkCard);
    } else {
      root.style.setProperty('--background-color', theme.backgroundColor || '#ffffff');
      root.style.setProperty('--text-color', theme.textColor || '#1f2937');
      root.style.setProperty('--card-background', '#ffffff');
      root.style.setProperty('--border-color', '#e5e7eb');
      root.style.setProperty('--surface-color', '#f9fafb');
    }
    
    root.style.setProperty('--button-color', theme.buttonColor || color1);
    root.style.setProperty('--button-text-color', theme.buttonTextColor || '#ffffff');
    
    // Set input border radius (configurable from theme)
    const inputBorderRadius = theme.inputBorderRadius || '8px';
    root.style.setProperty('--input-border-radius', inputBorderRadius);
    // Also set Material Design custom properties for form fields
    root.style.setProperty('--mat-form-field-outlined-container-shape', inputBorderRadius);
    root.style.setProperty('--mat-sys-corner-extra-small', inputBorderRadius);
  }

  getConfig(): AppConfig | null {
    return this.appConfig();
  }

  getTheme(): ThemeConfig | null {
    return this.appConfig()?.theme || null;
  }
}
